import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { withWorkEngagement } from './timer/workLogEngagement';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { formatMinutes } from './TimerSummaryModal';
import ConsumptionPreview, { type ConsumptionChoice } from './ConsumptionPreview';
import { useBillableBoxDisabled } from './worklogs/useBillableAuthority';
import WorkLogOverlapNotice from './worklogs/WorkLogOverlapNotice';
import { parallelConfirmTimes, shortenedTimes, type ShortenedTimes } from './worklogs/overlapModel';
import Dialog from './ui/Dialog';
import { toastDebtWarning } from '../utils/debtWarning';
import { toastWorkLogNotices } from '../utils/workLogNotices';
import { settleSubmit, useSubmitOnce } from '../hooks/useSubmitOnce';
import Field, { FormErrorSummary, rules, useFormValidation, type FormSchema } from './ui/Field';

/** A summary is required (the save always needed at least one character). */
const MANUAL_LOG_SCHEMA: FormSchema = { 'mwl-summary': [rules.required()] };

export interface ManualWorkLogModalProps {
  ticketId: string;
  ticketTitle?: string;
  /** Optional pre-filled start/end times (ISO). Defaults to a plausible window. */
  startTime?: string;
  endTime?: string;
  /** Display-only duration hint (minutes). Derived from times when omitted. */
  durationMinutes?: number;
  onClose: () => void;
  /** Called after a successful save so the caller can refresh/continue. */
  onSuccess: () => void;
}

/**
 * Legacy "add a manual work log" modal (used when closing a ticket without any
 * recorded work). Lets the user enter a summary (required), optionally mark the
 * entry billable, and persist it via POST /work-logs.
 *
 * This was originally the body of TimerSummaryModal before the Stage G refactor
 * repurposed that component for the timer stop/finish decision flow. The two
 * features are intentionally kept separate now.
 */
export default function ManualWorkLogModal({
  ticketId,
  ticketTitle,
  startTime,
  endTime,
  durationMinutes,
  onClose,
  onSuccess,
}: ManualWorkLogModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [summary, setSummary] = useState('');
  const [billable, setBillable] = useState(true);
  // BQ-069(a) (D-71): "לחיוב" is disabled for whoever the server would not honour it from.
  const billableLocked = useBillableBoxDisabled(ticketId);
  const [billableTouched, setBillableTouched] = useState(false);
  const [consumption, setConsumption] = useState<ConsumptionChoice>({});
  const [consumptionBlocked, setConsumptionBlocked] = useState(false);

  // Stable across renders (the consumption preview is keyed by these times).
  const [start] = useState(() => (startTime ? new Date(startTime) : new Date(Date.now() - 3600000)));
  const [end] = useState(() => (endTime ? new Date(endTime) : new Date()));
  const shownDuration = durationMinutes ?? Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));

  const attempt = useSubmitOnce(); // DQ-060: one Idempotency-Key per attempt (the browser lock is DQ-044)
  const mutation = useMutation({
    onSettled: (_d, err) => settleSubmit(attempt, err),
    // FU-53: a ticket without an engagement asks on which one to log (like "התחלת עבודה").
    // D-291: `arg` = the shortened times ("לקצר אוטומטית עד").
    mutationFn: (arg: ShortenedTimes | void) =>
      withWorkEngagement({ id: ticketId, title: ticketTitle }, (engagement_id) => api.createWorkLog({
        ticket_id: ticketId,
        start_time: (arg as ShortenedTimes | undefined)?.start_time ?? start.toISOString(),
        end_time: (arg as ShortenedTimes | undefined)?.end_time ?? end.toISOString(),
        summary: summary.trim(),
        billable: billableTouched ? billable : undefined,
        hour_bank_id: consumption.hour_bank_id,
        override_reason: consumption.override_reason?.trim() || undefined,
        engagement_id,
        confirm_parallel: (arg as ShortenedTimes | undefined)?.confirm_parallel || undefined, // D-299
      }, attempt.key)),
    onSuccess: (res) => {
      if (!res) return; // the engagement question was closed: nothing saved
      toastDebtWarning(res); // D-110
      toastWorkLogNotices(res); // D-266
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      onSuccess();
    },
  });

  const v = useFormValidation(MANUAL_LOG_SCHEMA, { 'mwl-summary': summary });
  const busy = mutation.isPending;

  const fmt = (d: Date) => format(d, 'dd/MM/yyyy HH:mm', { locale: he });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('manualLog.title')}
      description={ticketTitle ? <>{t('timer.ticket')} <bdi className="font-medium text-text">{ticketTitle}</bdi></> : undefined}
      size="md"
      dismissible={!busy}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1 sm:flex-none">{t('common.cancel')}</button>
          <button
            type="submit"
            form="manual-worklog-form"
            disabled={busy || consumptionBlocked}
            aria-busy={busy || undefined}
            className="btn btn-primary flex-1 sm:flex-none"
          >
            {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('manualLog.save')}
          </button>
        </>
      )}
    >
      <form id="manual-worklog-form" onSubmit={v.submit(() => mutation.mutate())} noValidate className="space-y-4">
        {v.showSummary && <FormErrorSummary errors={v.errors} labels={{ 'mwl-summary': t('form.workSummary') }} />}

        {/* The window being logged (fixed here; the caller chose it) */}
        <dl className="grid grid-cols-3 gap-3 rounded-lg bg-surface-subtle px-3 py-2.5 text-sm">
          <div>
            <dt className="text-xs text-text-2">{t('timer.start')}</dt>
            <dd className="tabular-nums text-text"><bdi dir="ltr">{fmt(start)}</bdi></dd>
          </div>
          <div>
            <dt className="text-xs text-text-2">{t('timer.end')}</dt>
            <dd className="tabular-nums text-text"><bdi dir="ltr">{fmt(end)}</bdi></dd>
          </div>
          <div>
            <dt className="text-xs text-text-2">{t('form.duration')}</dt>
            <dd className="font-medium tabular-nums text-text">{formatMinutes(shownDuration, t)}</dd>
          </div>
        </dl>

        <Field id="mwl-summary" label={t('form.workSummary')} required error={v.errors['mwl-summary']}>
          <textarea
            className="input min-h-20"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            onBlur={() => v.blur('mwl-summary')}
            placeholder={t('timer.summaryPlaceholder')}
            rows={3}
          />
        </Field>

        <label className={`inline-flex items-center gap-2 text-sm text-text ${billableLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
          title={billableLocked ? t('workLogs.billableLocked') : undefined}>
          <input
            type="checkbox"
            checked={billable}
            disabled={billableLocked}
            onChange={(e) => { setBillable(e.target.checked); setBillableTouched(true); }}
            className="h-4 w-4 accent-primary-600"
          />
          {t('timer.billableLabel')}
        </label>

        <ConsumptionPreview
          ticketId={ticketId}
          startTime={start.toISOString()}
          endTime={end.toISOString()}
          billable={billableTouched ? billable : undefined}
          value={consumption}
          onChange={setConsumption}
          onBlockedChange={setConsumptionBlocked}
          onAutoBillable={(b) => { if (!billableTouched) setBillable(b); }}
        />

        <WorkLogOverlapNotice
          error={mutation.error}
          fallback={t('timer.saveError')}
          busy={busy}
          onShorten={(c) => { const times = shortenedTimes(c); if (times) mutation.mutate(times); }}
          onConfirmParallel={(c) => { const times = parallelConfirmTimes(c); if (times) mutation.mutate(times); }}
          onRetry={() => mutation.mutate(mutation.variables)}
        />
      </form>
    </Dialog>
  );
}
